import { memo, useState } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { AppBar, Toolbar, Typography, useTheme } from '@mui/material';

import ErrorMessage from '@components/ErrorMessage';
import Loader from '@components/Loader';
import { useNegotiateWebsocketUrl } from '@services/queries';
import {
    MessageFromWebsocket,
    NegotiateWebsocketUrlHubs,
    NegotiateWebsocketUrlResponse,
} from '@services/types';

import { indexerTexts } from '../../../constants';

interface NotyficationFromWebsocketProps {
    data: NegotiateWebsocketUrlResponse;
}

const NotyficationFromWebsocket = ({ data: { url } }: NotyficationFromWebsocketProps) => {
    const theme = useTheme();
    const [error, setError] = useState<Error | null>(null);
    const { lastMessage, readyState } = useWebSocket(url, {
        onOpen: () => {
            setError(null);
        },
        onError: () => {
            setError(new Error(indexerTexts.errorFromWebsocket));
        },
    });

    if (readyState === ReadyState.CONNECTING || readyState === ReadyState.CLOSING)
        return <Loader />;

    if (error) return <ErrorMessage error={error} />;

    const getTextMessage = () => {
        if (readyState === ReadyState.CLOSED) return indexerTexts.connectionClosed;
        if (!lastMessage?.data)
            return readyState === ReadyState.OPEN ? indexerTexts.connectionOpen : null;

        try {
            const data = JSON.parse(lastMessage.data) as MessageFromWebsocket;
            return data.message;
        } catch {
            return indexerTexts.invalidMessage;
        }
    };

    return (
        <Typography variant='body2' color={theme.palette.customColors.black}>
            {getTextMessage()}
        </Typography>
    );
};

const IndexerStatus = () => {
    const theme = useTheme();
    const { data, isFetching, isError, error } = useNegotiateWebsocketUrl(
        NegotiateWebsocketUrlHubs.IndexerNotifications
    );

    const getContent = () => {
        if (isFetching) return <Loader />;
        if (isError) return <ErrorMessage error={error} />;
        if (!data) return null;
        return <NotyficationFromWebsocket data={data} />;
    };

    return (
        <AppBar position='sticky' sx={{ backgroundColor: theme.palette.customColors.lightGray }}>
            <Toolbar sx={{ gap: isFetching ? 0 : 1 }}>
                <Typography
                    variant='body2'
                    color={theme.palette.customColors.black}
                    whiteSpace='nowrap'
                >
                    {indexerTexts.title}
                </Typography>
                {getContent()}
            </Toolbar>
        </AppBar>
    );
};

export const MemoizedIndexerStatus = memo(IndexerStatus);
